import './histogram.scss';
import './datePicker.scss';
import {memo, useEffect, useRef, useState} from 'react';
import {BarChart} from './barChart';
import {Handles, Rail, Slider, Tracks} from 'react-compound-slider';
import {Handle, SliderRail, Track} from './components';
import {DatePicker} from '@mui/x-date-pickers'; // https://mui.com/x/react-date-pickers/date-picker
import dayjs, {Dayjs} from 'dayjs';
import {Stack} from '@mui/material';
import {t} from 'i18next';

export type HistogramSnapPoint = {
    value: string;
    count: number;
};

type HistogramProps = {
    snapPoints: HistogramSnapPoint[];
    range: number[];
    selection?: number[];
    handleSelection: (selection: number[]) => void;
};

export const Histogram = memo(({range, selection, handleSelection, snapPoints}: HistogramProps) => {
    const [chartValues, setChartValues] = useState<number[]>(selection || range);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        handleSelection([chartValues[0], chartValues[1]]);
    }, []);

    useEffect(() => {
        setChartValues(selection || range);
    }, [selection]);

    const handleStartDateChange = (value: Dayjs | null) => {
        if (value) {
            const unixTimestamp = value.startOf('day').valueOf(); // convert to milliseconds
            setChartValues([unixTimestamp, chartValues[1]]);
        } else {
            setChartValues([range[0], chartValues[1]]);
        }
    };

    const handleEndDateChange = (value: Dayjs | null) => {
        if (value) {
            const unixTimestamp = value.startOf('day').valueOf(); // convert to milliseconds
            setChartValues([chartValues[0], unixTimestamp]);
        } else {
            setChartValues([chartValues[0], range[1]]);
        }
    };

    return (
        <div className="histogram-wrapper">
            <div className="histogram">
                <BarChart data={snapPoints} highlight={chartValues} domain={range}/>
                <HistogramSlider
                    range={range}
                    handleSelection={handleSelection}
                    setChartValues={setChartValues}
                    values={chartValues}
                />
            </div>
            <HistogramSelection range={range} selection={chartValues}
                                handleStartDateChange={handleStartDateChange}
                                handleEndDateChange={handleEndDateChange}/>
        </div>
    );
});

interface HistogramSliderProps {
    range: number[];
    handleSelection: (selection: number[]) => void;
    setChartValues: (v: number[]) => void;
    values: number[];
}

export const HistogramSlider = memo(
    ({range, handleSelection, setChartValues, values}: HistogramSliderProps) => {
        return (
            <Slider
                mode={2}
                step={1}
                domain={range}
                className={'date-slider'}
                onSlideEnd={(v) => handleSelection([...v])}
                onChange={(v) => setChartValues([...v])}
                values={values}
            >
                <Rail>{({getRailProps}) => <SliderRail getRailProps={getRailProps}/>}</Rail>
                <Handles>
                    {({handles, getHandleProps}) => (
                        <div className="slider-handles">
                            {handles.map((handle) => (
                                <Handle
                                    key={handle.id}
                                    handle={handle}
                                    domain={range}
                                    getHandleProps={getHandleProps}
                                />
                            ))}
                        </div>
                    )}
                </Handles>
                <Tracks left={false} right={false}>
                    {({tracks, getTrackProps}) => (
                        <div className="slider-tracks">
                            {tracks.map(({id, source, target}) => (
                                <Track key={id} source={source} target={target} getTrackProps={getTrackProps}/>
                            ))}
                        </div>
                    )}
                </Tracks>
            </Slider>
        );
    }
);

interface HistogramSelectionProps {
    range: number[];
    selection: number[];
    handleStartDateChange: (value: Dayjs | null) => void;
    handleEndDateChange: (value: Dayjs | null) => void;
}

export function renderDatePicker({name, label, value, minDate, maxDate, onChange}) {
    return (
        <DatePicker name={name} label={label} value={value}
                    minDate={minDate ? dayjs(minDate) : undefined} maxDate={maxDate ? dayjs(maxDate) : undefined}
                    onChange={onChange} slotProps={{
            textField: {
                InputProps: {
                    classes: {
                        root: 'dpInputRoot',
                        notchedOutline: 'dpOutline'
                    }
                },
                readOnly: true
            }
        }}
        />
    );
}

export const HistogramSelection = memo(({
                                            range,
                                            selection,
                                            handleStartDateChange,
                                            handleEndDateChange
                                        }: HistogramSelectionProps) => {
    const startValue = selection[0] ? dayjs(selection[0]) : null;
    const endValue = selection[1] ? dayjs(selection[1]) : null;
    return (
        <div>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                {renderDatePicker({
                    name: 'startDate',
                    label: t('fieldTitle.tagsPlural.startDate.label'),
                    value: startValue,
                    minDate: range[0],
                    maxDate: endValue ?? range[1],
                    onChange: handleStartDateChange
                })}
                {renderDatePicker({
                    name: 'endDate',
                    label: t('fieldTitle.tagsPlural.endDate.label'),
                    value: endValue,
                    minDate: startValue ?? range[0],
                    maxDate: range[1],
                    onChange: handleEndDateChange
                })}
            </Stack>
        </div>
    );
});
